import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'latest';
  const [filteredArticles, setFilteredArticles] = useState(articles);

  useEffect(() => {
    if (activeTab === 'latest') {
      setFilteredArticles([...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } else {
      setFilteredArticles(articles.filter((a) => a.category === activeTab));
    }
  }, [activeTab]);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCategory = categories.find((c) => c.id === activeTab) || categories[0];

  const categorySchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `日本伦理d3tt - ${activeCategory.label}`,
      description: `浏览日本伦理d3tt ${activeCategory.label}分类下的所有相关内容，发现最新最优质的日本伦理d3tt资源。`,
      url: `https://jp-ethics-d3tt.readdy.site/category?tab=${activeTab}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jp-ethics-d3tt.readdy.site/' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://jp-ethics-d3tt.readdy.site/category' },
        { '@type': 'ListItem', position: 3, name: activeCategory.label },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`日本伦理d3tt ${activeCategory.label} - 日本伦理d3tt资源分类浏览`}
        description={`浏览日本伦理d3tt ${activeCategory.label}分类下的所有相关内容。日本伦理d3tt为您提供最新最全的日本伦理d3tt资源、在线内容、精选合集等。`}
        keywords={`日本伦理d3tt,日本伦理d3tt${activeCategory.label},日本伦理d3tt资源`}
        canonicalUrl={`https://jp-ethics-d3tt.readdy.site/category?tab=${activeTab}`}
        schemaData={categorySchema}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
              { label: activeCategory.label },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-6">
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                分类浏览
              </h1>
              <p className="text-sm text-foreground-500">
                按分类浏览日本伦理d3tt内容，选择您感兴趣的主题
              </p>
            </div>

            <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{filteredArticles.length}</strong> 篇相关内容
              </p>
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-700 font-medium backdrop-blur-sm">
                          {categories.find((c) => c.id === article.category)?.label || article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {article.views.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-inbox-line text-4xl text-foreground-300" />
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无内容</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}